import * as Tone from 'tone';
import { Seconds, TransportTime } from 'tone/build/esm/core/type/Units';
import { urlToNoteSequence } from '@magenta/music/es6/core';
import { NoteSequence } from '@magenta/music/es6/protobuf';
import { PlaybackState } from 'tone';
import BaseGenerator, { GeneratorOutput } from './generators/BaseGenerator';
import { clamp, toNearest } from './math-utils';
import EventEmitter from './EventEmitter';
import { BaseCurve } from './curves';
import {
  ChordProgression,
  Progress,
} from './types';
import PROGRESSIONS from './catalog/progressions';
// eslint-disable-next-line import/no-cycle
import MalakaiLayer, { IMalakaiLayer } from './MalakaiLayer';
import { loadMidiChordProgression } from './midi-utils';
import MalakaiTransport from './MalakaiTransport';

const DEFAULT_EVENT: MalakaiEventPayload = {
  time: 0,
  progress: 0,
};

export enum MalakaiEvent {
  /** Finished loading all assets */
  Load = 'load',
  /** Buffered more notes */
  BufferUpdate = 'bufferUpdate',
  /** Song playback triggered to start */
  PreStart = 'preStart',
  /** Song playback started */
  Start = 'start',
  /** Song has finished playback */
  End = 'end',
  /** Song was stopped and reset */
  Stop = 'stop',
}

export interface MalakaiConfig {
  /** Display name of config */
  name: string;
  /** Description of config */
  description?: string;
  /** Beats per minute */
  tempo: number;
  /** Song duration (example: '24m') */
  duration: TransportTime;
  /** Should the curve control points be rounded to the nearest bar? */
  quantizeCurve?: boolean;
  /** Available slider controls for the config */
  sliders?: MalakaiSlider[];
  /** Generators available to layers */
  generators?: MalakaiConfigGenerator[];
  /** Song playback layers */
  layers?: IMalakaiLayer[];
  /** Global playback events */
  hooks?: {[event in MalakaiEvent]?: MalakaiHookCallback};
}

export interface MalakaiContext {
  malakai: Malakai;
  transport: MalakaiTransport;
}

export interface MalakaiEventPayload {
  /** Time along the Malakai transport to schedule events */
  time: Seconds;
  /** Progress of the song at MalakaiEventPayload.time */
  progress: Progress;
  /** [MalakaiEvent.Note] Note frequency */
  frequency?: Tone.Unit.Frequency;
  /** [MalakaiEvent.Note] Note duration */
  duration?: Seconds;
}

export interface MalakaiHookCallback {
  (context: MalakaiContext, event: MalakaiEventPayload): void;
}

export interface MalakaiConfigGenerator {
  /** Unique name for the generator */
  name: string;
  /** URL to a midi file to use as the initial generator seed */
  lead: string;
  /** Note generator */
  generator: BaseGenerator;
}

export interface MalakaiSlider {
  /** Unique key for referencing the slider */
  key: string;
  /** Unique label for the slider */
  name: string;
  /** Default value (range [0,1]) */
  value: number;
  /** Minimum value (default 0.0) */
  min?: number;
  /** Maximum value (default 1.0) */
  max?: number;
  /** Size of incremenets between min and max (default 0.01) */
  step?: number;
  /** Description of slider function */
  description?: string;
}

export default class Malakai extends EventEmitter<Malakai, MalakaiEvent, MalakaiEventPayload> {
  transport: MalakaiTransport;

  curve!: BaseCurve;

  private config: MalakaiConfig;

  private layers = new Map<string, MalakaiLayer>();

  private leadSequences: {[name: string]: NoteSequence} = {};

  private chordProgressions: {[name: string]: ChordProgression} = {};

  private cancelGeneration: (() => void)|null = null;

  get state(): PlaybackState {
    return this.transport.state;
  }

  get progress(): number {
    return this.transport.progress;
  }

  get context(): MalakaiContext {
    return {
      malakai: this,
      transport: this.transport,
    };
  }

  constructor(
    curve: BaseCurve,
    config: MalakaiConfig,
  ) {
    super();
    this.emitterContext = this;
    Malakai.assertValidConfig(config);
    this.config = config;
    this.transport = new MalakaiTransport(this.config.duration, this.config.tempo);
    this.setCurve(curve);
  }

  getSlider(key: string): MalakaiSlider {
    const slider = this.config.sliders
      ? this.config.sliders.find((s) => s.key === key)
      : null;
    if (!slider) {
      throw new Error(`No slider with key '${key}' found.`);
    }
    return slider;
  }

  getSliderValue(key: string): number {
    return this.getSlider(key).value;
  }

  setSliderValue(key: string, value: number) {
    const slider = this.getSlider(key);
    slider.value = clamp(value, 0, 1);
  }

  setCurve(curve: BaseCurve) {
    const malakaiCurve = curve.clone();
    if (this.config.quantizeCurve) {
      const songDuration = this.transport.duration.toSeconds();
      const measureDuration = Tone.Time('1m').toSeconds();
      malakaiCurve.controls = malakaiCurve.controls.map(([x, y]) => ([
        toNearest(songDuration * x, measureDuration) / songDuration,
        y,
      ]));
    }
    this.curve = malakaiCurve;
  }

  getLayer(layerName: string): MalakaiLayer {
    const layer = this.layers.get(layerName);
    if (!layer) throw new Error(`Layer ${layerName} not found.`);
    return layer;
  }

  /** Enable/disable a layer. Disabled layers to not receive events */
  setLayerActive(layerName: string, active: boolean, time?: TransportTime) {
    const layer = this.layers.get(layerName);
    if (!layer) throw new Error(`Layer ${layerName} not found.`);
    if (time === undefined) {
      layer.active = active;
    } else {
      this.transport.scheduleOnce(() => {
        layer.active = active;
      }, time);
    }
  }

  setPosition(time: TransportTime) {
    this.transport.setPosition(time);
  }

  /** Find the closest matching chord progression for the given positivity and energy */
  findChordProgression(positivity: number, energy?: number): ChordProgression {
    const matches = Object.values(this.chordProgressions).sort((a, b) => {
      const aDiff = Math.abs(a.positivity - positivity)
        + (typeof energy === 'number' ? Math.abs(a.energy - energy) : 0);
      const bDiff = Math.abs(b.positivity - positivity)
        + (typeof energy === 'number' ? Math.abs(b.energy - energy) : 0);
      return aDiff - bDiff;
    });
    return matches[0];
  }

  async start() {
    console.log(`Generating ${this.transport.duration.toSeconds()} seconds...`);
    Tone.Destination.mute = false;
    if (this.transport.state === 'stopped') {
      this.emit(MalakaiEvent.PreStart, DEFAULT_EVENT);
      if (this.config.generators && this.config.generators.length) {
        const { cancel, promise } = this.generateAndSchedule();
        this.cancelGeneration = cancel;
        await promise;
        this.onStart();
      } else {
        this.onStart();
      }
    }
  }

  private onStart() {
    this.initializeSongEvents();
    this.transport.start();
    this.emit(MalakaiEvent.Start, DEFAULT_EVENT);
  }

  pause() {
    this.transport.pause();
  }

  resume() {
    this.transport.start();
  }

  stop() {
    if (this.transport.state !== 'stopped') {
      this.transport.stop();
      // Cancel active generators
      if (this.cancelGeneration) {
        this.cancelGeneration();
        this.cancelGeneration = null;
      }
      this.emit(MalakaiEvent.Stop, DEFAULT_EVENT);
    }
  }

  /** Load assets */
  async initialize() {
    await Tone.loaded();
    // Load leads
    if (this.config.generators) {
      const generatorsWithLeads: MalakaiConfigGenerator[] = Object.values(this.config.generators)
        .filter(({ lead }) => Boolean(lead));
      (await Promise.all(
        generatorsWithLeads.map(({ lead }) => urlToNoteSequence(lead)),
      )).forEach((sequence, index) => {
        this.leadSequences[generatorsWithLeads[index].lead] = sequence;
      });
    }
    // Load progressions
    const progressions = await Promise.all(
      PROGRESSIONS.map((url) => loadMidiChordProgression(url)),
    );
    progressions.forEach((p) => {
      this.chordProgressions[p.name] = p;
    });
    // Initialize generators
    if (this.config.generators && this.config.generators.length) {
      await Promise.all(
        this.config.generators.map(({ generator }) => {
          // eslint-disable-next-line no-param-reassign
          generator.context = this;
          return generator.initialize();
        }),
      );
    }
    // Initialize config.hooks
    if (this.config.hooks) {
      Object.entries(this.config.hooks).forEach(([type, callback]) => {
        if (callback) {
          this.on(type as MalakaiEvent, (event) => {
            callback(this.context, event);
          });
        }
      });
    }
    // Initialize layers
    if (this.config.layers) {
      const layers = await Promise.all(
        this.config.layers.map(
          (l) => new MalakaiLayer(this, this.transport, l, this.curve).initialize(),
        ),
      );
      layers.forEach((l) => {
        this.layers.set(l.name, l);
      });
    }
    this.emit(MalakaiEvent.Load, DEFAULT_EVENT);
  }

  private initializeSongEvents() {
    // Schedule End event
    this.transport.scheduleOnce((time) => {
      this.pause();
      this.emit(MalakaiEvent.End, { time, progress: 1 });
    }, Tone.Time(this.transport.duration).toSeconds());
    // Schedule layer events
    this.layers.forEach((l) => l.scheduleEvents());
  }

  private async* generateSequences(): AsyncGenerator<GeneratorOutput[]> {
    if (!this.config.generators || !this.config.generators.length) {
      throw new Error('No generators found in config.');
    }
    const iterators = this.config.generators.map(
      ({ lead, generator }) => generator
        .generate(this.leadSequences[lead], this.transport.duration.toSeconds()),
    );
    while (true) {
      // eslint-disable-next-line no-await-in-loop
      const generatorResults = await Promise.all(iterators.map((i) => i.next()));
      const generatorOutputs: GeneratorOutput[] = [];
      generatorResults.forEach(({ value, done }, index) => {
        if (!done) {
          generatorOutputs.push({
            sequence: value.sequence,
            meta: {
              ...value.meta,
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              generator: this.config!.generators![index].name,
            },
          });
        }
      });
      if (generatorOutputs.length) {
        yield generatorOutputs;
      } else {
        return;
      }
    }
  }

  private scheduleSequences(generatorOutputs: GeneratorOutput[]) {
    const progress = Math.min(...generatorOutputs.map(({ meta }) => meta.progress));
    generatorOutputs.forEach(({ sequence, meta }) => {
      [...this.layers.values()].forEach((layer) => {
        if (layer.generator === meta.generator) {
          layer.triggerNotes(sequence.notes);
        }
      });
    });
    const time = this.transport.getTimeForProgress(progress);
    this.emit(MalakaiEvent.BufferUpdate, { time, progress });
  }

  private generateAndSchedule(): { promise: Promise<void>; cancel: () => void } {
    let isCancelled = false;
    return {
      promise: (async () => {
        // eslint-disable-next-line no-restricted-syntax
        for await (const generatedSequences of this.generateSequences()) {
          if (isCancelled) return;
          this.scheduleSequences(generatedSequences);
        }
      })(),
      cancel() {
        isCancelled = true;
      },
    };
  }

  static assertValidConfig(config: MalakaiConfig) {
    const layers = config.layers ? Object.values(config.layers) : [];
    layers.forEach((layer) => {
      if (layer.generator) {
        const generator = config.generators?.find(({ name }) => name === layer.generator);
        if (!generator) throw new Error(`No generator named ${layer.generator} configured.`);
      }
    });
  }
}
